import type { Column, ColumnDef, RowData } from '@tanstack/react-table';
import type { CSSProperties } from 'react';

/** Get display text for a column header. Used in filter labels, CSV export, column visibility. */
export function getColumnHeaderText(column: Column<any, unknown>): string {
  const meta = column.columnDef.meta;
  return (
    meta?.filterLabel ??
    (typeof column.columnDef.header === 'string'
      ? column.columnDef.header
      : column.id)
  );
}

/**
 * The column's visible header name: a plain-text header as written, else
 * `meta.filterLabel` (the plain name of a rendered header), else the id. Used
 * where the name stands for the header itself (sort and ⓘ labels, the label's
 * hover title); `getColumnHeaderText` stays the name of the column's filter.
 */
export function getColumnLabelText(column: Column<any, unknown>): string {
  const header = column.columnDef.header;
  return typeof header === 'string'
    ? header
    : (column.columnDef.meta?.filterLabel ?? column.id);
}

/**
 * Find a column definition by its resolved TanStack id. Matches an explicit
 * `id` before `accessorKey`, mirroring TanStack's own `id ?? accessorKey`
 * resolution — every caller here passes a `ColumnFilter.id` (already the
 * resolved id), so a column declaring both { id, accessorKey } is found by its
 * `id`, and an unrelated column whose `accessorKey` collides with that id no
 * longer shadows it.
 */
export function findColumnById(columns: ColumnDef<any, any>[], id: string) {
  return columns.find(
    (c) =>
      ('id' in c && c.id === id) ||
      ('accessorKey' in c && c.accessorKey === id),
  );
}

/** Sentinel value for columns that should flex to fill remaining space */
export const FLEX_COLUMN_SIZE = Number.MAX_SAFE_INTEGER;

/**
 * Default `minSize` WmsTable injects on a flex column whose def doesn't
 * declare one. A flex column renders `width:'auto'`, so without a real
 * min-width floor it collapses to a sliver once the other columns' fixed
 * widths fill the container — and, being force-set `enableHiding:false`, it
 * has no column-manager escape hatch. 200 matches the long-standing implicit
 * base width `getEffectiveColumnBaseSize` already used for flex columns in
 * the 'even'/'first'/'last' space modes.
 */
export const DEFAULT_FLEX_MIN_WIDTH = 200;

/**
 * Base width for a column before any 'even'/'first'/'last' space-distribution
 * extra is applied. Mirrors the flex-sentinel detection in `getColumnStyle`:
 * a flex-sentinel column's `getSize()` returns the `FLEX_COLUMN_SIZE`
 * placeholder, which must never reach CSS as a literal width — callers that
 * need a real px baseline for it (leftover-space math in 'even' mode, or
 * rendering a flex-sentinel column that ISN'T the chosen absorber in
 * 'first'/'last' mode) fall back to its declared `minSize` (or
 * `DEFAULT_FLEX_MIN_WIDTH` if unset).
 */
export function getEffectiveColumnBaseSize(
  column: Column<any, unknown>,
): number {
  return column.columnDef.size === FLEX_COLUMN_SIZE
    ? (column.columnDef.minSize ?? DEFAULT_FLEX_MIN_WIDTH)
    : column.getSize();
}

/** Options controlling how `getColumnStyle` distributes extra space (D-spaceMode). */
export interface ColumnStyleOptions {
  /**
   * Id of the column that should absorb leftover space (width:'auto') —
   * used in 'first'/'last' mode. When omitted (and `extraPerColumn` is also
   * omitted), falls back to today's flex-sentinel detection ('flex' mode).
   */
  absorbColumnId?: string;
  /** Extra px added to every column's base width — used in 'even' mode. */
  extraPerColumn?: number;
}

/** Get inline styles for a column — applied to BOTH <th> and <td> */
export function getColumnStyle(
  column: Column<any, unknown>,
  options?: ColumnStyleOptions,
): CSSProperties {
  const minSize = column.columnDef.minSize;
  const meta = column.columnDef.meta;
  // Read from the def, not getSize(): today getSize() returns the def size
  // for a flex column (FLEX_COLUMN_SIZE), so this is equivalent — but once
  // columnResizeMode is active, getSize() for a resized column returns the
  // sized value, so reading columnDef.size is unambiguous and immune to any
  // future sizing write.
  const isFlexSentinel = column.columnDef.size === FLEX_COLUMN_SIZE;

  // Which column (if any) absorbs leftover space via width:'auto':
  //  - options.absorbColumnId set → 'first'/'last' mode: only that exact
  //    column id absorbs (a flex-sentinel column not chosen never does).
  //  - options.extraPerColumn set (absorbColumnId absent) → 'even' mode:
  //    nothing absorbs; every column gets a px width instead.
  //  - neither set → default ('flex' mode, and every existing caller that
  //    doesn't pass options) — EXACT today's behavior: the flex-sentinel
  //    column absorbs.
  const isAbsorbing =
    options?.absorbColumnId !== undefined
      ? column.id === options.absorbColumnId
      : options?.extraPerColumn !== undefined
        ? false
        : isFlexSentinel;

  const width = isAbsorbing
    ? 'auto'
    : getEffectiveColumnBaseSize(column) + (options?.extraPerColumn ?? 0);

  return {
    width,
    // Advisory only: under `table-layout: fixed` browsers exclude cell
    // min-width from the column-sizing algorithm (a squeezed width:'auto'
    // column renders 0px regardless — verified in Chrome, #1186). The floor
    // that actually holds is WmsTable's table-level min-width (the sum of
    // effective base sizes), which forces the wrapper to scroll instead of
    // letting the absorber collapse. Kept as a hint for non-fixed contexts.
    ...(isAbsorbing && minSize ? { minWidth: minSize } : {}),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: meta?.align ?? 'left',
  };
}

/** True if a column def is the flex fill column. */
export function isFlexColumn(col: ColumnDef<any, any>): boolean {
  return (col as any).size === FLEX_COLUMN_SIZE;
}

/** Floor for pointer-drag column resizing (D13) — see column-prefs.ts R6. */
export const MIN_COLUMN_WIDTH = 48;

// ── Client-side filter functions ────────────────────────────────

/** Text filter: supports contains, equals, startsWith, endsWith, notEquals operators */
const textFilterFn = (
  row: any,
  columnId: string,
  filterValue: unknown,
): boolean => {
  if (!filterValue || typeof filterValue !== 'object') return true;
  const { operator, value } = filterValue as {
    operator: string;
    value: string;
  };
  if (!value) return true;

  const cellValue = String(row.getValue(columnId) ?? '').toLowerCase();
  const search = value.toLowerCase();

  switch (operator) {
    case 'contains':
      return cellValue.includes(search);
    case 'equals':
      return cellValue === search;
    case 'startsWith':
      return cellValue.startsWith(search);
    case 'endsWith':
      return cellValue.endsWith(search);
    case 'notEquals':
      return cellValue !== search;
    default:
      return cellValue.includes(search);
  }
};
textFilterFn.autoRemove = (val: unknown) =>
  !val ||
  (typeof val === 'object' &&
    val !== null &&
    !(val as any).value?.toString().trim());

/** Number filter: supports eq, gt, lt, gte, lte, between */
export const numberFilterFn = (
  row: any,
  columnId: string,
  filterValue: unknown,
): boolean => {
  if (!filterValue || typeof filterValue !== 'object') return true;
  const { operator, value } = filterValue as {
    operator: string;
    value: number | [number, number];
  };

  const cellValue = Number(row.getValue(columnId));
  if (isNaN(cellValue)) return false;

  if (operator === 'between' && Array.isArray(value)) {
    const [min, max] = value;
    if (isNaN(min) || isNaN(max)) return true;
    return cellValue >= min && cellValue <= max;
  }

  const numValue = Number(value);
  if (isNaN(numValue)) return true;
  switch (operator) {
    case 'equals':
      return cellValue === numValue;
    case 'gt':
      return cellValue > numValue;
    case 'lt':
      return cellValue < numValue;
    case 'gte':
      return cellValue >= numValue;
    case 'lte':
      return cellValue <= numValue;
    default:
      return true;
  }
};
numberFilterFn.autoRemove = (val: unknown) =>
  !val ||
  (typeof val === 'object' &&
    val !== null &&
    !Array.isArray((val as any).value) &&
    ((val as any).value == null || (val as any).value === ''));

/** Date filter: supports after, before, between, equals */
const dateFilterFn = (
  row: any,
  columnId: string,
  filterValue: unknown,
): boolean => {
  if (!filterValue || typeof filterValue !== 'object') return true;
  const { operator, value } = filterValue as {
    operator: string;
    value: string | [string, string];
  };
  if (!value) return true;

  const cellDate = new Date(row.getValue(columnId) as string).getTime();
  if (isNaN(cellDate)) return false;

  if (operator === 'between' && Array.isArray(value)) {
    const [from, to] = value;
    return (
      cellDate >= new Date(from).getTime() && cellDate <= new Date(to).getTime()
    );
  }

  const compareDate = new Date(value as string).getTime();
  switch (operator) {
    case 'after':
      return cellDate > compareDate;
    case 'before':
      return cellDate < compareDate;
    case 'equals':
      return (
        new Date(row.getValue(columnId) as string).toDateString() ===
        new Date(value as string).toDateString()
      );
    default:
      return true;
  }
};
dateFilterFn.autoRemove = (val: unknown) =>
  !val || (typeof val === 'object' && val !== null && !(val as any).value);

/** Enum filter: checks if cell value is in the selected values array */
const enumFilterFn = (
  row: any,
  columnId: string,
  filterValue: unknown,
): boolean => {
  if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0)
    return true;
  const cellValue = String(row.getValue(columnId) ?? '');
  return filterValue.includes(cellValue);
};
enumFilterFn.autoRemove = (val: unknown) =>
  !val || (Array.isArray(val) && val.length === 0);

/** Map from filterType to filterFn */
const FILTER_FN_MAP: Record<string, any> = {
  text: textFilterFn,
  number: numberFilterFn,
  date: dateFilterFn,
  enum: enumFilterFn,
  boolean: enumFilterFn,
};

/**
 * Returns a new column defs array with filterFn auto-assigned from meta.filterType.
 * Does not mutate the original array.
 * Called by WmsTable during setup. For columns that have meta.filterType but no explicit filterFn,
 * assigns the correct filterFn from the map.
 */
export function autoAssignFilterFns<TData extends RowData>(
  columns: ColumnDef<TData, any>[],
): ColumnDef<TData, any>[] {
  return columns.map((col) => {
    const meta = (col as any).meta;
    const hasFilterFn = 'filterFn' in col;
    if (meta?.filterType && !hasFilterFn) {
      return { ...col, filterFn: FILTER_FN_MAP[meta.filterType] ?? undefined };
    }
    return col;
  });
}
